import { inject, Injectable } from '@angular/core';
import { collection, collectionData, doc, Firestore, query, where, writeBatch } from '@angular/fire/firestore';
import { Collections, Environment, Flag, Group } from '@ffx/common';
import { catchError, from, Observable, of, switchMap, take } from 'rxjs';
import { DbProject } from '../../interfaces/db-interfaces';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
    providedIn: 'root'
})
export class FlagsService {
    private fireStore = inject(Firestore);

    getFeatureFlagsByEnvironmentId(environmentId: string): Observable<any> {
        const featureFlagCollection = collection(this.fireStore, Collections.featureFlags);
        const getFeatureFlagsQuery = query(featureFlagCollection, where('environmentId', '==', environmentId));
        return collectionData(getFeatureFlagsQuery, { idField: 'id' });
    }

    addFlags(
        project: DbProject,
        environments: Environment[],
        flags: Flag[],
        selectedEnvironment: Environment
    ): Observable<any> {
        if (!flags || !flags.length) {
            return of(true);
        }
        // If we don't have any groups, then the environments are the default environments
        if (project.groups.length === 1) {
            return this.addFlagsToEnvironments(environments, flags, selectedEnvironment);
        }
        // Else, we need to add the flags to ALL environments in ALL groups (including default)
        const environmentCollection = collection(this.fireStore, Collections.environment);
        const groupEnvironmentQuery = query(
            environmentCollection,
            where(
                'groupId',
                'in',
                project.groups.map((group) => group.id)
            )
        );
        return collectionData(groupEnvironmentQuery, { idField: 'id' }).pipe(
            take(1),
            switchMap((response) => {
                const groupEnvironments = response.map((groupEnvironment) => ({
                    name: groupEnvironment['name'],
                    id: groupEnvironment['id'],
                    cssColour: groupEnvironment['cssColour'],
                    isProd: groupEnvironment['isProd'] ?? false
                }));
                return this.addFlagsToEnvironments(groupEnvironments, flags, selectedEnvironment);
            })
        );
    }

    deleteFlags(flags: Flag[]): Observable<any> {
        if (!flags || !flags.length) {
            return of(true);
        }
        const deleteBatch = writeBatch(this.fireStore);
        flags.forEach((flag) => {
            // Only delete flags from this environment
            deleteBatch.delete(doc(this.fireStore, `${Collections.featureFlags}/${flag.id}`));
        });
        return from(deleteBatch.commit());
    }

    updateFlags(groups: Group[], flagsToUpdate: Flag[]): Observable<any> {
        if (!flagsToUpdate || !flagsToUpdate.length) {
            return of(true);
        }
        const groupIds = groups.map((group) => group.id);
        const environmentsCollection = collection(this.fireStore, Collections.environment);
        const flagsCollection = collection(this.fireStore, Collections.featureFlags);
        const environmentQuery = query(environmentsCollection, where('groupId', 'in', groupIds));

        return collectionData(environmentQuery, { idField: 'id' }).pipe(
            take(1),
            switchMap((response) => {
                if (!response.length) {
                    console.warn('No environments found for the provided groups.');
                    return of(false);
                }
                const environmentIds = response.map((groupEnvironment) => groupEnvironment['id']);
                const flagsToUpdateKeys = flagsToUpdate.map((flag) => flag.key);
                if (!environmentIds.length || !flagsToUpdateKeys.length) {
                    console.warn('No environments found for the provided groups.');
                    return of(false);
                }
                const flagsQuery = query(
                    flagsCollection,
                    where('environmentId', 'in', environmentIds),
                    where('key', 'in', flagsToUpdateKeys)
                );
                return collectionData(flagsQuery, { idField: 'id' }).pipe(
                    take(1),
                    switchMap((flags) => {
                        if (!flags || !Array.isArray(flags) || !flags.length) {
                            console.warn('No matching flags found to update.');
                            return of(false);
                        }
                        const updateBatch = writeBatch(this.fireStore);
                        flags.forEach((flag) => {
                            const updatedFlag = flagsToUpdate.find((f) => f.key === flag['key']);
                            if (updatedFlag) {
                                updateBatch.update(doc(this.fireStore, `${Collections.featureFlags}/${flag['id']}`), {
                                    name: updatedFlag.name,
                                    description: updatedFlag.description,
                                    labels: updatedFlag.labels,
                                    isOn: updatedFlag.id === flag.id ? updatedFlag.status : flag['isOn'],
                                    environmentId: flag['environmentId']
                                });
                            }
                        });
                        return from(updateBatch.commit()).pipe(take(1));
                    })
                );
            }),
            catchError(() => {
                console.error('Error updating flags.');
                return of(false);
            })
        );
    }

    addFlagsToEnvironments(
        environments: Environment[],
        flagsToAdd: Flag[],
        selectedEnvironment: Environment
    ): Observable<any> {
        const addBatch = writeBatch(this.fireStore);
        environments.forEach((environment: Environment) => {
            flagsToAdd.forEach((flag) => {
                addBatch.set(doc(this.fireStore, `${Collections.featureFlags}/${uuidv4()}`), {
                    key: flag.key,
                    name: flag.name,
                    description: flag.description,
                    labels: flag.labels,
                    isOn: selectedEnvironment.id === environment.id ? flag.status : false,
                    environmentId: environment.id
                });
            });
        });
        return from(addBatch.commit());
    }
}
